import type { ParsedUrlQuery } from "querystring";

/**
 * The listing page in a URL, in its two forms:
 * - **public** — `?page=2`, 1-indexed. The clean PLP URL *is* page 1.
 * - **internal** — `/dames~p2/kleding`. ISR cache keys come from the pathname
 *   alone and `getStaticProps` never sees query params, so the page can only
 *   reach the server render through the path. Marking an *existing* segment
 *   leaves the resolved Next route — and so shallow routing — untouched.
 */

export const PAGE_QUERY_PARAM = "page";
export const MAX_PAGINATION_PAGE = 50;

const PAGE_MARKER = /\/?~p(\d+)(?=\/|$)/;

export function parsePaginatedUrlPage(
  raw: string | null | undefined
): number | null {
  if (!raw || !/^\d+$/.test(raw)) {
    return null;
  }

  const page = Number(raw);

  return page >= 2 ? page : null;
}

export function urlPageToIndex(raw: unknown): number {
  const value =
    typeof raw === "string" || typeof raw === "number" ? String(raw) : null;

  return (parsePaginatedUrlPage(value) ?? 1) - 1;
}

export function indexToUrlPage(index: number): number {
  return index + 1;
}

export function buildPageMarker(page: number): string {
  return `~p${page}`;
}

export function readPageMarker(value: string): number | null {
  const match = PAGE_MARKER.exec(value);

  return match ? Number(match[1]) : null;
}

function stripPageMarker(value: string): string {
  return value.replace(PAGE_MARKER, "");
}

type ParamValue = string | string[] | undefined;

export function stripPageMarkerFromParam<T extends ParamValue>(
  value: T
): { value: T; page: number | null } {
  const isList = Array.isArray(value);
  const path = isList ? value.join("/") : value;

  if (typeof path !== "string") {
    return { value, page: null };
  }

  const page = readPageMarker(path);

  if (page === null) {
    return { value, page: null };
  }

  const stripped = stripPageMarker(path);

  return {
    value: (isList ? stripped.split("/").filter(Boolean) : stripped) as T,
    page,
  };
}

export function stripPageMarkerFromQuery<T extends ParsedUrlQuery>(
  query: T | undefined
): { query: T; page: number | null } {
  const cleaned: ParsedUrlQuery = { ...query };
  let page: number | null = null;

  for (const [key, value] of Object.entries(cleaned)) {
    const result = stripPageMarkerFromParam(value);

    if (result.page !== null) {
      page = result.page;
      cleaned[key] = result.value;
    }
  }

  return { query: cleaned as T, page };
}

export function toPublicAsPath(asPath: string): string {
  const [rawPathname, rawQuery = ""] = asPath.split("?");
  const page = readPageMarker(rawPathname);

  if (page === null) {
    return asPath;
  }

  const params = new URLSearchParams(rawQuery);

  if (page >= 2) {
    params.set(PAGE_QUERY_PARAM, String(page));
  } else {
    params.delete(PAGE_QUERY_PARAM);
  }

  const pathname = stripPageMarker(rawPathname) || "/";
  const query = params.toString();

  return query ? `${pathname}?${query}` : pathname;
}
